import { useEffect, useRef, useState } from 'react';
import {
  Camera,
  CameraOff,
  Check,
  RefreshCw,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface CameraCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCapture: (file: File) => void;
}

export function CameraCaptureModal({
  open,
  onOpenChange,
  onCapture,
}: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mobileInputRef = useRef<HTMLInputElement | null>(null);

  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);

  const isMobile =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  const stopActiveStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // ignore
        }
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async (mode: 'environment' | 'user') => {
    stopActiveStream();
    setError(null);
    setIsStarting(true);

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function'
    ) {
      setError('Camera API is not supported in this browser or context.');
      setIsStarting(false);
      return;
    }

    try {
      // Check available cameras
      if (navigator.mediaDevices.enumerateDevices) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoInputs = devices.filter((d) => d.kind === 'videoinput');
          setHasMultipleCameras(videoInputs.length > 1);
        } catch {
          // non-critical
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch (err: unknown) {
      const errName = (err as { name?: string })?.name || '';
      if (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (errName === 'NotFoundError' || errName === 'DevicesNotFoundError') {
        setError('No camera device found on this system.');
      } else if (errName === 'NotReadableError' || errName === 'TrackStartError') {
        setError('Camera is in use by another application or tab.');
      } else {
        setError('Could not access camera. Please check camera permissions.');
      }
    } finally {
      setIsStarting(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPreviewUrl(null);
      setCapturedFile(null);
      startCamera(facingMode);
    } else {
      stopActiveStream();
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setCapturedFile(null);
      setError(null);
    }

    return () => {
      stopActiveStream();
    };
  }, [open]);

  const handleFlipCamera = () => {
    const nextMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(nextMode);
    startCamera(nextMode);
  };

  const handleCaptureSnapshot = () => {
    const video = videoRef.current;
    if (!video) return;

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], 'repaired.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        const url = URL.createObjectURL(blob);
        stopActiveStream();
        setPreviewUrl(url);
        setCapturedFile(file);
      },
      'image/jpeg',
      0.9,
    );
  };

  const handleRetake = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setCapturedFile(null);
    startCamera(facingMode);
  };

  const handleConfirmPhoto = () => {
    if (capturedFile) {
      onCapture(capturedFile);
      onOpenChange(false);
    }
  };

  // Fallback trigger ONLY on mobile devices if WebRTC fails
  const handleMobileFallbackFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const renamed = new File([file], 'repaired.jpg', {
        type: file.type || 'image/jpeg',
        lastModified: Date.now(),
      });
      onCapture(renamed);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 overflow-hidden bg-background">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <DialogTitle className="text-base font-semibold inline-flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary" />
              Device Camera Capture
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              Strictly camera-only. Photograph the repaired cycle parts.
            </DialogDescription>
          </div>
          {!previewUrl && !error && (
            <Badge tone="success" className="text-[10px] gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live Camera
            </Badge>
          )}
        </div>

        {/* Viewfinder / Preview View */}
        <div className="relative bg-black aspect-[4/3] max-h-[380px] sm:max-h-[420px] flex items-center justify-center overflow-hidden">
          {previewUrl ? (
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={previewUrl}
                alt="Captured repaired cycle preview"
                className="w-full h-full object-contain"
              />
              <div className="absolute top-3 left-3">
                <Badge tone="success" className="text-xs gap-1 shadow-md">
                  <Sparkles className="h-3 w-3" /> Photo Captured
                </Badge>
              </div>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-white/90 space-y-3 max-w-sm">
              <CameraOff className="h-10 w-10 mx-auto text-rose-400 opacity-80" />
              <p className="text-xs text-rose-200 font-medium">{error}</p>
              <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs text-white border-white/30 hover:bg-white/10"
                  onClick={() => startCamera(facingMode)}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry Camera
                </Button>
                {/* On mobile only, provide direct native camera trigger */}
                {isMobile && (
                  <>
                    <input
                      ref={mobileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleMobileFallbackFile}
                    />
                    <Button
                      size="sm"
                      className="text-xs"
                      onClick={() => mobileInputRef.current?.click()}
                    >
                      <Camera className="h-3.5 w-3.5 mr-1" /> Open Mobile Camera
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="relative w-full h-full flex items-center justify-center">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />

              {/* Viewfinder brackets overlay */}
              <div className="pointer-events-none absolute inset-6 border border-white/25 rounded-md">
                <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-white/80" />
                <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-white/80" />
                <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-white/80" />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-white/80" />
              </div>

              {isStarting && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <div className="text-center text-white/80 space-y-2">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-primary" />
                    <p className="text-xs">Initializing camera...</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Controls Footer */}
        <div className="p-4 bg-surface-elevated border-t border-border flex items-center justify-between">
          {previewUrl ? (
            <div className="w-full flex items-center justify-between gap-3">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={handleRetake}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Retake Photo
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={handleConfirmPhoto}
              >
                <Check className="h-3.5 w-3.5" /> Use This Photo
              </Button>
            </div>
          ) : (
            <div className="w-full flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>

              <div className="flex items-center gap-3">
                {/* Switch camera button (if multiple cameras or on mobile) */}
                {(hasMultipleCameras || isMobile) && !error && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs h-9 px-3"
                    onClick={handleFlipCamera}
                    title="Switch camera"
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> Flip Camera
                  </Button>
                )}

                {/* Shutter Capture Button */}
                <Button
                  variant="primary"
                  size="sm"
                  disabled={isStarting || Boolean(error)}
                  className="gap-2 h-10 px-5 text-xs font-semibold rounded-full shadow-md"
                  onClick={handleCaptureSnapshot}
                >
                  <Camera className="h-4 w-4" />
                  Capture Photo
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
